import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { pluralizeIssues } from '../../../../../../shared/helpers';
import { isSortSelected, ProjectSortPrefixes } from '../../../../../../shared/projectsV2';
import * as ProjectsV2Store from '../../../../../redux/projectsV2';
import { nuc } from '../../../../../utils';
import { RouterLink } from '../../../../generic';
import Tooltip from '../../../../generic/Tooltip';
import type { Project, ProjectsSort } from '../../../../../../shared/projectsV2';

const SELECTED_CLASS = 'project-item__issue-counts_selected';

/**
 * Represents the different types of issue counts that can be associated with a project.
 */
type IssueCountsType = 'licensing' | 'security' | 'quality' | 'total';

/**
 * Represents the issue category names used for routing / navigation.
 */
type IssueNavType = 'licensing' | 'vulnerability' | 'quality';

/**
 * Helper for determining the issue summary message to display for a project.
 */
function determineIssueSummaryMessage(project: Project, activeType: IssueCountsType): string {
  if (project.latestBuildStatus === 'failed') {
    return 'Unavailable';
  }

  if (project.issues.total === null) {
    return 'Unscanned';
  }

  const issuesTotal = project.issues[activeType];
  if (issuesTotal === null) {
    return '--';
  }

  return `${issuesTotal} ${activeType !== 'total' ? activeType : ''} ${pluralizeIssues(issuesTotal)}`;
}

/**
 * Helper for determining the issue summary tooltip to display for a project.
 */
function determineIssueSummaryTooltip(project: Project, activeType: IssueCountsType): string {
  return determineIssueSummaryMessage(project, activeType);
}

/**
 * Determine the issue URL for a project based on the project state and selected issue counts type.
 */
function determineIssueUrl(project: Project, issueCountsType: IssueCountsType, issueNavType: IssueNavType): string {
  const baseProjectUrl = `/projects/${nuc(project.id)}`;
  if (project.latestBuildStatus === 'failed') {
    return baseProjectUrl;
  }

  if (project.version === null) {
    return baseProjectUrl;
  }

  if (issueCountsType === 'total') {
    return baseProjectUrl;
  }

  return `${baseProjectUrl}/refs/branch/${nuc(project.branch)}/${nuc(project.version)}/issues/${issueNavType}`;
}

type Props = {
  project: Project;
};

const ProjectIssueCounts: VoidFunctionComponent<Props> = ({ project }: Props) => {
  const sort: ProjectsSort = useSelector(ProjectsV2Store.selectors.getSort);

  let issueCountsType: IssueCountsType = 'total';
  let issueNavType: IssueNavType = 'licensing';

  if (isSortSelected(sort, ProjectSortPrefixes.Licensing)) {
    issueCountsType = 'licensing';
  } else if (isSortSelected(sort, ProjectSortPrefixes.Security)) {
    issueCountsType = 'security';
    issueNavType = 'vulnerability';
  } else if (isSortSelected(sort, ProjectSortPrefixes.Quality)) {
    issueCountsType = 'quality';
    issueNavType = 'quality';
  }

  const issueUrl = determineIssueUrl(project, issueCountsType, issueNavType);
  const issueSummaryMessage = determineIssueSummaryMessage(project, issueCountsType);
  const issueSummaryTooltip = determineIssueSummaryTooltip(project, issueCountsType);

  return (
    <div className="project-item__issue-counts">
      <Tooltip id="total-issues-tooltip" tooltip={issueSummaryTooltip}>
        <RouterLink className={SELECTED_CLASS} to={issueUrl}>
          {issueSummaryMessage}
        </RouterLink>
      </Tooltip>
    </div>
  );
};

export default ProjectIssueCounts;
